import { useDispatch, useSelector } from 'react-redux';
import { Button, Grid, Typography } from '@mui/material';
import { Box } from '@mui/system';
import isEqual from 'lodash.isequal';

import SideDrawer from 'Lib/components/layout/components/SideDrawer';
import { DRAWER_CONTENT_TYPES } from 'Lib/components/layout/components/SideDrawer';
import Dialog from 'Lib/muiComponents/dialog/Dialog';
import { screenSize } from 'Lib/utilities/screenSize';

import FilterMap from '../../table/filters/fields/FieldMap';

export default function Filters({ activeFilters, actions, api, filters, formik, filterRerenderCount, setFilterRenderCount }) {
    const dispatch = useDispatch();

    const { drawerContentTypeID, isDrawerOpen } = useSelector(state => state.ui);

    const { mobile: isMobileScreenSize, tablet: isTabletScreenSize } = screenSize();
    const mobile = isMobileScreenSize || isTabletScreenSize;

    const handleCancel = () => {
        if (formik.dirty) {
            // Set form values back to original values
            formik.setValues(activeFilters);
            // Triggers a re-load of the entire form with the original values
            setFilterRenderCount(prevCount => prevCount + 1);
        }
    };

    const handleClear = () => {
        // Wipe out values
        formik.setValues({});
        // Triggers a re-load of the entire form with the original values
        setFilterRenderCount(prevCount => prevCount + 1);
    };

    const handleChange = (filterName, value) => {
        if (!value || (Array.isArray(value) && !value.length)) {
            const filters = { ...formik.values };
            delete filters[filterName];
            formik.setValues(filters);
        } else formik.setFieldValue(filterName, value);
    };

    const renderForm = () => (
        <form key={filterRerenderCount} onSubmit={formik.handleSubmit}>
            <Grid container spacing={4} sx={{ pb: 4 }}>
                {filters.buttons?.map((button, index) => {
                    const Component = FilterMap[button.type];
                    const value = formik.values[button.name];
                    const min = formik.values[button.minName] ?? null;
                    const max = formik.values[button.maxName] ?? null;

                    return Component ? (
                        <Grid item xs={12} key={`${button.name}_${index}`}>
                            <Component api={api} value={value} onChange={handleChange} min={min} max={max} {...button} />
                        </Grid>
                    ) : null;
                })}
            </Grid>
        </form>
    );

    return mobile ? (
        <Dialog
            open={isDrawerOpen && drawerContentTypeID === DRAWER_CONTENT_TYPES.FILTERS}
            toggle={() => dispatch(actions.toggleDrawer())}
            actions={() => (
                <Box display="flex" justifyContent="space-between" sx={{ width: '100%' }}>
                    <Box>
                        <Button variant="tertiary" aria-label="clear" onClick={handleClear}>
                            Clear
                        </Button>
                    </Box>
                    <Box>
                        <Button variant="secondary" aria-label="cancel" onClick={handleCancel} sx={{ mr: 4 }}>
                            Cancel
                        </Button>
                        <Button variant="primary" aria-label="update" type="submit" onClick={formik.handleSubmit} disabled={isEqual(formik.initialValues, formik.values)}>
                            Update
                        </Button>
                    </Box>
                </Box>
            )}
            onSubmit={formik.handleSubmit}
        >
            <Typography sx={{ fontSize: theme => theme.typography.pxToRem(20), letterSpacing: '0.15px', pb: 4 }}>Filter Options</Typography>
            {renderForm()}
        </Dialog>
    ) : (
        <SideDrawer
            pinnable
            title="Filter Options"
            onClear={handleClear}
            onCancel={handleCancel}
            onSubmit={formik.handleSubmit}
            isSubmitDisabled={isEqual(formik.initialValues, formik.values)}
            drawerContentTypeID={DRAWER_CONTENT_TYPES.FILTERS}
        >
            {renderForm()}
        </SideDrawer>
    );
}
